import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { observer } from "mobx-react";
import { I18N_NS } from "Config/i18n";
import { EditableTable, EditableColumnProps } from "Components/General/EditableTable";
import { Tabs } from "antd";
import { AccessRightsSelect } from "./AccessRightsSelect";
import { stringSort } from "utils/defaultSort";
import { viewAccessRightsStore } from "Pages/Project/Views/Stores/AccessRightsStore";
import { contactsStore } from "Stores/ContactsStore";
import { groupListStore } from "Pages/Project/Groups/Stores/GroupsStore";
import { AssigneeUserSelect, AssigneeGroupSelect } from "Pages/Project/Documents/Components/AssigneeIdSelect";
import { InactiveAssigneeText } from "Components/General/InactiveAssigneeText";

interface AccessRightsProps extends WithTranslation, ModalComponentProps {
  viewId: number;
}

export const AccessRights = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<AccessRightsProps> {
      componentDidMount() {
        (async () => {
          viewAccessRightsStore.setViewId(this.props.viewId);

          if (contactsStore.state === "RAW") {
            await contactsStore.fetch();
          }

          if (!groupListStore.list.length) {
            await groupListStore.fetch();
          }

          await viewAccessRightsStore.fetch();
        })();
      }

      componentWillUnmount() {
        viewAccessRightsStore.clear();
      }

      handleDelete = async (id: number) => {
        await viewAccessRightsStore.deleteRight(id);
      };

      render() {
        const { t } = this.props;
        const groupTableColumns: EditableColumnProps<API_FolderRightsEntry>[] = [
          {
            title: t("Name"),
            dataIndex: "assigneeId",
            editable: true,
            defaultSortOrder: "ascend",
            inputType: "custom",
            render: (text, record) => (
              <InactiveAssigneeText removed={record.removed} projectMembershipActive={record.projectMembershipActive}>
                {record.name}
              </InactiveAssigneeText>
            ),
            sorter: (a, b) => stringSort(a, b, "name"),
            customInput: AssigneeGroupSelect,
            inputProps: {
              placeholder: t("Select assignee"),
              style: { width: "100%" },
              optionFilterProp: "children",
              showSearch: true,
            },
          },
          {
            title: t("Access right"),
            width: 230,
            dataIndex: "accessLevel",
            editable: true,
            sorter: (a, b) => stringSort(a, b, "accessLevel"),
            inputType: "custom",
            customInput: AccessRightsSelect,
            inputProps: {
              placeholder: t("Select access right"),
              style: { width: "100%" },
            },
          },
        ];

        const userTableColumns: EditableColumnProps<API_FolderRightsEntry>[] = [
          {
            title: t("Name"),
            dataIndex: "assigneeId",
            editable: true,
            defaultSortOrder: "ascend",
            inputType: "custom",
            render: (text, record) => (
              <InactiveAssigneeText removed={record.removed} projectMembershipActive={record.projectMembershipActive}>
                {record.name}
              </InactiveAssigneeText>
            ),
            sorter: (a, b) => stringSort(a, b, "name"),
            customInput: AssigneeUserSelect,
            inputProps: {
              placeholder: t("Select assignee"),
              style: { width: "100%" },
              optionFilterProp: "children",
              showSearch: true,
            },
          },
          {
            title: t("Access right"),
            width: 230,
            dataIndex: "accessLevel",
            editable: true,
            sorter: (a, b) => stringSort(a, b, "accessLevel"),
            inputType: "custom",
            customInput: AccessRightsSelect,
            inputProps: {
              placeholder: t("Select access right"),
              style: { width: "100%" },
            },
          },
        ];

        return (
          <div>
            <Tabs
              type="card"
              items={[
                {
                  key: "1",
                  label: t("Groups"),
                  children: (
                    <EditableTable<API_FolderRightsEntry>
                      name="projectAccessRights"
                      rowIdKey="id"
                      rowKey="id"
                      loading={viewAccessRightsStore.state === "LOADING"}
                      columns={groupTableColumns}
                      dataSource={viewAccessRightsStore.groupRights.slice()}
                      rowEditable={false}
                      rowDeletable={true}
                      onDelete={this.handleDelete}
                      onAddRow={viewAccessRightsStore.addRight}
                      showAddRow={true}
                      style={{ width: "100%" }}
                      scroll={{ x: 1000 }}
                    />
                  ),
                },
                {
                  key: "2",
                  label: t("Additional single users"),
                  children: (
                    <EditableTable<API_FolderRightsEntry>
                      name="projectUserAccessRights"
                      rowIdKey="id"
                      rowKey="id"
                      loading={viewAccessRightsStore.state === "LOADING"}
                      columns={userTableColumns}
                      dataSource={viewAccessRightsStore.usersRights.slice()}
                      rowEditable={false}
                      rowDeletable={true}
                      onDelete={this.handleDelete}
                      onAddRow={viewAccessRightsStore.addRight}
                      showAddRow={true}
                      style={{ width: "100%" }}
                      scroll={{ x: 1000 }}
                    />
                  ),
                },
              ]}
            />
          </div>
        );
      }
    }
  )
);
